import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import {
  Mail,
  Users,
  Calendar,
  Shield,
  Settings,
  PenSquare,
  Sun,
  Moon,
  LogOut,
  Bell,
  PanelLeftOpen,
  PanelLeftClose,
  Sparkles,
} from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useThemeStore } from '@/shared/state/themeStore';
import { usePreferencesStore } from '@/shared/state/preferencesStore';
import { useAuthStore } from '@/shared/state/authStore';
import { useAuth } from '@/shared/auth/AuthProvider';
import { useEmailStore } from '@/shared/state/emailStore';
import { SyncStatus } from './SyncStatus';
import styles from './Sidebar.module.css';

import { lazy, Suspense, useState } from 'react';
const AskPrufeDrawer = lazy(() => import('@/modules/ai/AskPrufeDrawer').then(m => ({ default: m.AskPrufeDrawer })));

const NAV_ITEMS = [
  { path: '/mail', icon: Mail, labelKey: 'nav.email' },
  { path: '/contacts', icon: Users, labelKey: 'nav.contacts' },
  { path: '/calendar', icon: Calendar, labelKey: 'nav.calendar' },
] as const;

const BOTTOM_ITEMS = [
  { path: '/admin', icon: Shield, labelKey: 'nav.admin' },
  { path: '/settings', icon: Settings, labelKey: 'nav.settings' },
] as const;

export function Sidebar() {
  const { t } = useTranslation();
  const { theme, setTheme } = useThemeStore();
  const { sidebarExpanded, toggleSidebar } = usePreferencesStore();
  const { signOut } = useAuth();
  const email = useAuthStore((s) => s.email);
  const isDark = theme === 'dark' || (theme === 'system' && !window.matchMedia('(prefers-color-scheme: light)').matches);
  const unreadCount = useEmailStore(s => s.mailboxes.find(m => m.role === 'inbox')?.unreadEmails ?? 0);
  const [askPrufeOpen, setAskPrufeOpen] = useState(false);

  const expanded = sidebarExpanded;

  // When expanded, show labels inline — no tooltips needed
  const NavTooltip = expanded
    ? ({ children }: { children: React.ReactNode }) => <>{children}</>
    : Tooltip.Root;

  return (
    <Tooltip.Provider delayDuration={400}>
      <nav className={clsx(styles.sidebar, expanded && styles.sidebarExpanded)} aria-label={t('nav.email')}>
        {/* Expand/collapse toggle */}
        <button className={styles.expandBtn} onClick={toggleSidebar} aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}>
          {expanded ? <PanelLeftClose size={16} strokeWidth={1.5} /> : <PanelLeftOpen size={16} strokeWidth={1.5} />}
        </button>

        {/* Logo */}
        <div className={styles.logo}>
          <img src="/logo-small.png" alt="Prufe" className={styles.logoImg} />
          <span className={styles.logoText}>Prufe</span>
        </div>

        {/* Compose button */}
        {expanded ? (
          <NavLink to="/mail/compose" className={styles.compose} aria-label={t('email.compose')}>
            <PenSquare size={20} strokeWidth={1.5} />
            <span className={styles.composeLabel}>Compose</span>
          </NavLink>
        ) : (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <NavLink to="/mail/compose" className={styles.compose} aria-label={t('email.compose')}>
                <PenSquare size={20} strokeWidth={1.5} />
              </NavLink>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content className={styles.tooltip} side="right" sideOffset={8}>
                {t('email.compose')}
                <Tooltip.Arrow className={styles.tooltipArrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )}

        {/* Main nav */}
        <div className={styles.navGroup}>
          {NAV_ITEMS.map((item) => {
            const badge = item.path === '/mail' && unreadCount > 0 ? unreadCount : 0;
            return expanded ? (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => clsx(styles.navItem, isActive && styles.navItemActive)}
                aria-label={t(item.labelKey)}
              >
                <item.icon size={20} strokeWidth={1.5} />
                <span className={styles.navLabel}>{t(item.labelKey)}</span>
                {badge > 0 && <span className={styles.navBadge}>{badge > 99 ? '99+' : badge}</span>}
              </NavLink>
            ) : (
              <Tooltip.Root key={item.path}>
                <Tooltip.Trigger asChild>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => clsx(styles.navItem, isActive && styles.navItemActive)}
                    aria-label={t(item.labelKey)}
                  >
                    <item.icon size={20} strokeWidth={1.5} />
                  </NavLink>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content className={styles.tooltip} side="right" sideOffset={8}>
                    {t(item.labelKey)}{badge > 0 && ` (${badge})`}
                    <Tooltip.Arrow className={styles.tooltipArrow} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            );
          })}
        </div>

        {/* Notification bell */}
        {expanded ? (
          <NavLink to="/mail" className={({ isActive }) => clsx(styles.navItem, isActive && styles.navItemActive)} style={{ position: 'relative' }}>
            <Bell size={20} strokeWidth={1.5} />
            <span className={styles.navLabel}>Notifications</span>
            {unreadCount > 0 && <span className={styles.navBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
          </NavLink>
        ) : (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <NavLink to="/mail" className={({ isActive }) => clsx(styles.navItem, isActive && styles.navItemActive)} style={{ position: 'relative' }}>
                <Bell size={20} strokeWidth={1.5} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', insetBlockStart: '2px', insetInlineEnd: '2px',
                    minWidth: '16px', height: '16px', borderRadius: '8px',
                    background: 'var(--color-accent-danger)', color: 'white',
                    fontSize: '10px', fontWeight: 700, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', padding: '0 4px',
                  }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </NavLink>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content className={styles.tooltip} side="right" sideOffset={8}>
                {unreadCount > 0 ? `${unreadCount} unread` : 'No unread emails'}
                <Tooltip.Arrow className={styles.tooltipArrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )}

        {/* Ask Prufe AI button */}
        {expanded ? (
          <button className={styles.navItem} onClick={() => setAskPrufeOpen(true)} aria-label={t('ai.askPrufe', 'Ask Prufe')} data-testid="ask-prufe-btn">
            <Sparkles size={20} strokeWidth={1.5} />
            <span className={styles.navLabel}>{t('ai.askPrufe', 'Ask Prufe')}</span>
          </button>
        ) : (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button className={styles.navItem} onClick={() => setAskPrufeOpen(true)} aria-label={t('ai.askPrufe', 'Ask Prufe')} data-testid="ask-prufe-btn">
                <Sparkles size={20} strokeWidth={1.5} />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content className={styles.tooltip} side="right" sideOffset={8}>
                {t('ai.askPrufe', 'Ask Prufe')}
                <Tooltip.Arrow className={styles.tooltipArrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )}

        <div className={styles.spacer} />

        {/* Bottom nav */}
        <div className={styles.navGroup}>
          {BOTTOM_ITEMS.map((item) => (
            expanded ? (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => clsx(styles.navItem, isActive && styles.navItemActive)}
                aria-label={t(item.labelKey)}
              >
                <item.icon size={20} strokeWidth={1.5} />
                <span className={styles.navLabel}>{t(item.labelKey)}</span>
              </NavLink>
            ) : (
              <Tooltip.Root key={item.path}>
                <Tooltip.Trigger asChild>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => clsx(styles.navItem, isActive && styles.navItemActive)}
                    aria-label={t(item.labelKey)}
                  >
                    <item.icon size={20} strokeWidth={1.5} />
                  </NavLink>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content className={styles.tooltip} side="right" sideOffset={8}>
                    {t(item.labelKey)}
                    <Tooltip.Arrow className={styles.tooltipArrow} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            )
          ))}
        </div>

        {/* Sync status */}
        <SyncStatus />

        {/* Theme toggle */}
        {expanded ? (
          <button className={styles.navItem} onClick={() => setTheme(isDark ? 'light' : 'dark')} aria-label={isDark ? t('settings.lightMode') : t('settings.darkMode')}>
            {isDark ? <Sun size={20} strokeWidth={1.5} /> : <Moon size={20} strokeWidth={1.5} />}
            <span className={styles.navLabel}>{isDark ? 'Light mode' : 'Dark mode'}</span>
          </button>
        ) : (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button className={styles.navItem} onClick={() => setTheme(isDark ? 'light' : 'dark')} aria-label={isDark ? t('settings.lightMode') : t('settings.darkMode')}>
                {isDark ? <Sun size={20} strokeWidth={1.5} /> : <Moon size={20} strokeWidth={1.5} />}
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content className={styles.tooltip} side="right" sideOffset={8}>
                {isDark ? t('settings.lightMode') : t('settings.darkMode')}
                <Tooltip.Arrow className={styles.tooltipArrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )}

        {/* Logout */}
        {expanded ? (
          <button className={styles.navItem} onClick={signOut} aria-label={t('auth.logout')}>
            <LogOut size={20} strokeWidth={1.5} />
            <span className={styles.navLabel}>{t('auth.logout')}</span>
          </button>
        ) : (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button className={styles.navItem} onClick={signOut} aria-label={t('auth.logout')}>
                <LogOut size={20} strokeWidth={1.5} />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content className={styles.tooltip} side="right" sideOffset={8}>
                {t('auth.logout')}{email && <> — {email}</>}
                <Tooltip.Arrow className={styles.tooltipArrow} />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )}
      </nav>

      {/* Ask Prufe AI Drawer (lazy-loaded) */}
      {askPrufeOpen && (
        <Suspense fallback={null}>
          <AskPrufeDrawer open={askPrufeOpen} onClose={() => setAskPrufeOpen(false)} />
        </Suspense>
      )}
    </Tooltip.Provider>
  );
}
